// Phase 10B — Appointment list card restyled in the AutoMind 2050 design
// language. All role gates, props, and child components preserved.

import React from 'react';
import {
  Calendar as CalendarIcon,
  Clock,
  Phone,
  User,
  Trash2,
  AlertCircle,
} from 'lucide-react';
import { StatusPill } from '../../components/ui/status-pill';
import {
  APPOINTMENT_TYPE_LABELS,
  SOURCE_LABELS,
  CALENDAR_SYNC_LABELS,
  REMINDER_STATUS_LABELS,
} from './appointmentConstants';
import AppointmentStatusActions from './AppointmentStatusActions';
import { useAuth } from '../../auth/AuthContext';

// Phase 6C — roles treated as "editors" for calendar/SMS actions.
const EDITOR_ROLES = new Set([
  'platform_admin',
  'dealer_owner',
  'dealer_manager',
  'admin',
  'owner',
  'manager',
]);

const statusTone = (status) => {
  switch (status) {
    case 'confirmed':
      return 'success';
    case 'scheduled':
      return 'info';
    case 'completed':
      return 'neutral';
    case 'cancelled':
      return 'danger';
    case 'no_show':
      return 'warning';
    case 'needs_review':
      return 'danger';
    default:
      return 'neutral';
  }
};

const confidenceTone = (level) => {
  switch (level) {
    case 'high':
      return 'success';
    case 'medium':
      return 'warning';
    case 'low':
      return 'danger';
    default:
      return 'neutral';
  }
};

const calendarSyncTone = (status) => {
  switch (status) {
    case 'synced':
      return 'success';
    case 'failed':
      return 'danger';
    case 'skipped':
      return 'neutral';
    case 'not_synced':
    default:
      return 'neutral';
  }
};

const reminderTone = (status) => {
  switch (status) {
    case 'sent':
      return 'success';
    case 'failed':
      return 'danger';
    case 'scheduled':
      return 'info';
    case 'skipped':
      return 'neutral';
    case 'not_scheduled':
    default:
      return 'neutral';
  }
};

const getTypeIcon = (type) => {
  const icons = {
    callback: Phone,
    sales_meeting: User,
    service: Clock,
    demo: CalendarIcon,
    consultation: User,
  };
  return icons[type] || CalendarIcon;
};

const directionDot = (callDirection) =>
  callDirection === 'inbound' ? 'bg-emerald-500' : 'bg-sky-500';

const formatDate = (dateString) =>
  new Date(dateString).toLocaleDateString('en-US', {
    weekday: 'short',
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });

const formatTime = (timeString) => {
  if (!timeString) return 'Time TBD';
  const [hours, minutes] = timeString.split(':').map(Number);
  const period = hours >= 12 ? 'PM' : 'AM';
  const displayHours = hours % 12 || 12;
  const displayMinutes = String(minutes).padStart(2, '0');
  return `${displayHours}:${displayMinutes} ${period}`;
};

// Redact phone numbers to ***<last4>.
const redactPhone = (phone) => {
  if (!phone) return '—';
  const digits = String(phone).replace(/\D/g, '');
  if (digits.length < 4) return '***';
  return `***${digits.slice(-4)}`;
};

const AppointmentListCard = ({
  appointment,
  onUpdateStatus,
  onDelete,
  canEdit = true,
  onChanged,
}) => {
  const { user } = useAuth();
  const apt = appointment;
  const TypeIcon = getTypeIcon(apt.type);

  const typeLabel =
    APPOINTMENT_TYPE_LABELS[apt.appointment_type] || apt.type || null;
  const isAiSourced = apt.source === 'ai_created' || apt.source === 'ai_suggested';
  const isEditor = Boolean(user && EDITOR_ROLES.has(user.role));

  return (
    <div className="row-2050 flex items-center justify-between p-4 rounded-lg border border-ink-200/60 dark:border-ink-800/60 hover:bg-ink-50/60 dark:hover:bg-ink-900/40 transition-colors">
      <div className="flex items-center gap-4 min-w-0 flex-1">
        <div className="flex items-center gap-2 min-w-0">
          <span
            className={`w-2 h-2 rounded-full ${directionDot(apt.call_direction)} flex-shrink-0`}
            title={`${apt.call_direction === 'inbound' ? 'Inbound' : 'Outbound'} call`}
          />
          <TypeIcon className="w-4 h-4 text-ink-500 flex-shrink-0" />
          <div className="min-w-0">
            <div className="font-medium text-sm truncate">{apt.customer_name}</div>
            <div className="text-xs text-ink-500 nums">
              {redactPhone(apt.customer_phone)}
            </div>
          </div>
        </div>

        <div className="text-xs hidden md:block">
          <div className="text-ink-700 dark:text-ink-200 nums">
            {formatDate(apt.appointment_date)}
          </div>
          <div className="text-ink-500 nums">
            {formatTime(apt.appointment_time)} · {apt.duration_minutes}m
          </div>
        </div>

        <div className="flex items-center gap-1.5 flex-wrap">
          {typeLabel && (
            <StatusPill tone="neutral" label={typeLabel} />
          )}
          <StatusPill tone={statusTone(apt.status)} dot label={apt.status} />
          {isAiSourced && (
            <>
              <StatusPill tone="info" label={SOURCE_LABELS[apt.source]} />
              {apt.confidence_level && (
                <StatusPill
                  tone={confidenceTone(apt.confidence_level)}
                  label={apt.confidence_level}
                />
              )}
            </>
          )}
          {apt.needs_review === true && (
            <span
              className="pill-2050 pill-danger inline-flex items-center gap-1"
              title={apt.review_reason || 'This appointment needs review'}
            >
              <AlertCircle className="w-3 h-3" />
              Review
            </span>
          )}
          {apt.calendar_sync_status && (
            <StatusPill
              tone={calendarSyncTone(apt.calendar_sync_status)}
              label={CALENDAR_SYNC_LABELS[apt.calendar_sync_status]}
            />
          )}
          {apt.reminder_status && (
            <StatusPill
              tone={reminderTone(apt.reminder_status)}
              label={REMINDER_STATUS_LABELS[apt.reminder_status]}
            />
          )}
        </div>

        {apt.notes && (
          <div className="text-xs text-ink-500 max-w-xs truncate hidden lg:block">
            {apt.notes}
          </div>
        )}
      </div>

      <div className="flex flex-col items-end gap-2 flex-shrink-0">
        <AppointmentStatusActions
          appointment={apt}
          canEdit={canEdit}
          isEditor={isEditor}
          onChanged={
            onChanged || (() => onUpdateStatus && onUpdateStatus(apt.id, apt.status))
          }
        />

        <div className="flex items-center gap-1">
          {apt.status === 'scheduled' && (
            <button
              type="button"
              className="btn-2050 btn-ghost"
              onClick={() => onUpdateStatus(apt.id, 'confirmed')}
            >
              Confirm
            </button>
          )}
          {(apt.status === 'scheduled' || apt.status === 'confirmed') && (
            <button
              type="button"
              className="btn-2050 btn-ghost"
              onClick={() => onUpdateStatus(apt.id, 'completed')}
            >
              Complete
            </button>
          )}
          <button
            type="button"
            className="btn-2050 btn-ghost"
            onClick={() => onDelete(apt.id)}
          >
            <Trash2 className="w-4 h-4" />
          </button>
        </div>
      </div>
    </div>
  );
};

export default AppointmentListCard;
